import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ANALYTICS_API_ENDPOINT = 'https://api.limkc.com/visitor/analytics';

const Analytics = () => {
  const { user, loading: authLoading } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30); // Default 30 days

  // Check if user has admin/owner membership
  const userMembership = user?.['custom:membership'];
  const isAdmin = userMembership === 'admin' || userMembership === 'owner';

  useEffect(() => {
    if (!isAdmin) return;

    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, isAdmin]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${ANALYTICS_API_ENDPOINT}?days=${dateRange}`);

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        console.error('Failed to fetch analytics:', response.status);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ minHeight: '100vh' }}>
        <div className="loader-spinner"></div>
      </div>
    );
  }

  // CRITICAL: Block access if not admin/owner (AFTER hooks)
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <Helmet>
        <title>Analytics Dashboard - LimKC</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="w-full h-full overflow-auto">
        {/* ANALYTICS DASHBOARD SECTION */}
        <section className="section" style={{ paddingTop: 'calc(var(--navbar-height) + 2rem)' }}>
          <div className="section-container">
            {/* Header */}
            <div className="section-header mb-8">
              <h2 className="section-title">Visitor Analytics Dashboard</h2>
              <p className="section-description">
                Real-time visitor tracking and statistics
              </p>
            </div>

          {/* Date Range Selector */}
          <div className="mb-8 flex flex-wrap gap-3">
            <button
              onClick={() => setDateRange(7)}
              className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                dateRange === 7
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Last 7 Days
            </button>
            <button
              onClick={() => setDateRange(30)}
              className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                dateRange === 30
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Last 30 Days
            </button>
            <button
              onClick={() => setDateRange(90)}
              className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                dateRange === 90
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Last 90 Days
            </button>
            <button
              onClick={() => setDateRange(365)}
              className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                dateRange === 365
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Last Year
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="loader-spinner mx-auto mb-4"></div>
              <p className="text-secondary">Loading analytics...</p>
            </div>
          ) : analytics ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Nappa Leather Themed Card */}
                <div className="p-6 rounded-xl shadow-lg transition-all hover:shadow-xl" style={{
                  background: 'linear-gradient(135deg, #C19A6B 0%, #A67B5B 100%)',
                  border: '1px solid rgba(212, 165, 116, 0.3)'
                }}>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#F5E6D3' }}>Total Visits</h3>
                  <p className="text-4xl font-bold" style={{ color: '#FFF8DC' }}>
                    {analytics.total_visits?.toLocaleString() || 0}
                  </p>
                </div>

                <div className="card-glass p-6">
                  <h3 className="text-lg font-semibold mb-2 text-secondary">Unique Visitors</h3>
                  <p className="text-4xl font-bold" style={{ color: 'var(--color-accent-green)' }}>
                    {analytics.unique_visitors?.toLocaleString() || 0}
                  </p>
                </div>

                <div className="card-glass p-6">
                  <h3 className="text-lg font-semibold mb-2 text-secondary">Countries</h3>
                  <p className="text-4xl font-bold" style={{ color: 'var(--color-accent-orange)' }}>
                    {Object.keys(analytics.by_country || {}).length}
                  </p>
                </div>

                <div className="card-glass p-6">
                  <h3 className="text-lg font-semibold mb-2 text-secondary">Pages Visited</h3>
                  <p className="text-4xl font-bold" style={{ color: 'var(--color-accent-purple)' }}>
                    {Object.keys(analytics.by_page || {}).length}
                  </p>
                </div>
              </div>

              {/* Visitors by Country */}
              <div className="card-glass p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4">Visitors by Country</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                        <th className="text-left py-3 px-4">Country</th>
                        <th className="text-right py-3 px-4">Visits</th>
                        <th className="text-right py-3 px-4">Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(analytics.by_country || {})
                        .sort(([, a], [, b]) => b - a)
                        .map(([country, count]) => (
                          <tr key={country} className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                            <td className="py-3 px-4 font-semibold">{country}</td>
                            <td className="text-right py-3 px-4">{count}</td>
                            <td className="text-right py-3 px-4 text-secondary">
                              {((count / analytics.total_visits) * 100).toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Top Pages */}
              <div className="card-glass p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4">Top Pages</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                        <th className="text-left py-3 px-4">Page</th>
                        <th className="text-right py-3 px-4">Visits</th>
                        <th className="text-right py-3 px-4">Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(analytics.by_page || {})
                        .sort(([, a], [, b]) => b - a)
                        .map(([page, count]) => (
                          <tr key={page} className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                            <td className="py-3 px-4 font-mono">{page}</td>
                            <td className="text-right py-3 px-4">{count}</td>
                            <td className="text-right py-3 px-4 text-secondary">
                              {((count / analytics.total_visits) * 100).toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Device Breakdown */}
              <div className="card-glass p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4">Device Breakdown</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(analytics.by_device || {}).map(([device, count]) => (
                    <div key={device} className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                      <h3 className="text-lg font-semibold capitalize mb-2">{device}</h3>
                      <p className="text-3xl font-bold" style={{ color: 'var(--color-accent-blue)' }}>
                        {count}
                      </p>
                      <p className="text-sm text-secondary mt-1">
                        {((count / analytics.total_visits) * 100).toFixed(1)}% of total
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Visits */}
              <div className="card-glass p-6">
                <h2 className="text-2xl font-bold mb-4">Recent Visits (Last 20)</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                        <th className="text-left py-3 px-4">Timestamp</th>
                        <th className="text-left py-3 px-4">Page</th>
                        <th className="text-left py-3 px-4">Location</th>
                        <th className="text-left py-3 px-4">Device</th>
                        <th className="text-left py-3 px-4">Referrer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(analytics.recent_visits || []).map((visit, index) => (
                        <tr key={index} className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                          <td className="py-3 px-4 text-sm">
                            {new Date(visit.timestamp).toLocaleString()}
                          </td>
                          <td className="py-3 px-4 font-mono text-sm">{visit.page}</td>
                          <td className="py-3 px-4 text-sm">
                            {visit.city}, {visit.country}
                          </td>
                          <td className="py-3 px-4 text-sm capitalize">{visit.device}</td>
                          <td className="py-3 px-4 text-sm truncate max-w-xs">
                            {visit.referrer === 'direct' ? (
                              <span className="text-secondary">Direct</span>
                            ) : (
                              <a
                                href={visit.referrer}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:underline"
                                style={{ color: 'var(--color-accent-blue)' }}
                              >
                                {new URL(visit.referrer).hostname}
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-secondary">No analytics data available</p>
            </div>
          )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Analytics;
